import { FC, useState, useEffect } from 'react';
import { productDataTransformer, Slot } from '@commerce-product-webcomponents/sdk';
import { useTranslation } from '@nextgen-web-framework/all';
import type { ProductData, DerivedAvailabilityStatus } from '@commerce-product-webcomponents/sdk';
import { CardType } from '../../Molecules';
import type {
  BundleComponentPropsTypes,
  BundleDataType,
  IBundleState,
  ProductDataType,
  SlotDataType,
} from './types/propsTypes';
import { SlotType, Operation } from './types/enums';
import {
  sortBundleData,
  updateBundleStateHandler,
  getLinearBundleState,
  replaceSlotPoolInstructionPlaceholders,
  replaceSlotVariantInstructionPlaceholders,
  checkForAllPoolConditionMatched,
  updateProductQtyVariant,
} from './bundleComponentUtil';
import { BundleProductCard } from './BundleProductCard';
import styles from './BundleComponent.module.scss';

const BundleComponent: FC<BundleComponentPropsTypes> = ({
  bundle,
  onBundleProductSelected,
  bundleStoredState = [],
  showSubText,
  dateSubText,
  skipChecksForButton = false,
  showZeroAsFree,
}: BundleComponentPropsTypes): React.JSX.Element => {
  let formattedBundleData = sortBundleData(bundle);
  const [bundleDataState, setBundleDataState] = useState<BundleDataType>(formattedBundleData.bundle as BundleDataType); // props which is getting passed from parent
  const [findFixedIndex, setFindFixedIndex] = useState<number | undefined>(formattedBundleData.foundFixedIndex);
  const t = useTranslation();

  useEffect(() => {
    if (bundle && bundle.slots?.length) {
      formattedBundleData = sortBundleData(bundle);
      setBundleDataState(formattedBundleData.bundle as BundleDataType);
      setFindFixedIndex(formattedBundleData.foundFixedIndex);
    }
    onBundleProductSelected(
      getLinearBundleState(formattedBundleData.bundle as BundleDataType),
      { value: false },
      checkForAllPoolConditionMatched(formattedBundleData.bundle as BundleDataType),
      checkForTotalMinPoolQty(),
      { value: false },
      false,
    );
    checkForCacheBundleData(formattedBundleData.bundle as BundleDataType);
  }, [bundle]);

  useEffect(() => {
    checkForCacheBundleData();
  }, [bundleStoredState]);

  const checkForTotalMinPoolQty = () =>
    bundleDataState?.slots?.reduce((sum: number, slot: Slot) => {
      if (slot.type == SlotType.POOL) {
        return sum + Number(slot.minQty);
      } else {
        return sum;
      }
    }, 0) || 0;

  const checkForCacheBundleData = (bundleStructuredData?: BundleDataType) => {
    if (bundleStoredState && bundleStoredState.length > 0) {
      checkForBundleDataChange(bundleStructuredData || bundleDataState, bundleStoredState);
    }
  };

  const checkForBundleDataChange = (bundleStructuredData: BundleDataType, bundleStoredData: IBundleState[]) => {
    let slots = [...(bundleStructuredData?.slots as SlotDataType[])];
    let updatedSlots: SlotDataType[] = [];
    bundleStoredData?.forEach((bundle: IBundleState) => {
      updatedSlots = slots?.map((slot: SlotDataType) => {
        if (bundle.slotId === slot.id) {
          return {
            ...slot,
            totalQty: bundle.totalQty,
            isPoolConditionMatched: Number(slot.minQty) <= Number(bundle.totalQty),
            products: slot.products?.map((product: ProductDataType) => updateProductQtyVariant(product, bundle)),
          };
        } else {
          return { ...slot };
        }
      });
      slots = updatedSlots;
    });
    const newBundleState = { ...bundleStructuredData, slots: updatedSlots };
    setBundleDataState(newBundleState);
    const isAllPoolConditionMatched = checkForAllPoolConditionMatched(newBundleState);
    onBundleProductSelected(
      getLinearBundleState(newBundleState as BundleDataType),
      { value: isAllPoolConditionMatched },
      checkForAllPoolConditionMatched(newBundleState),
      checkForTotalMinPoolQty(),
      { value: true },
      false,
    );
  };

  const quantityHandler = (productId: string, opration: Operation, slot: Slot) => {
    const { isAllPoolConditionMatched, updatedBundleState } = updateBundleStateHandler(
      productId,
      opration,
      slot,
      bundleDataState,
    );
    const newLinearBundleState = getLinearBundleState(updatedBundleState);
    onBundleProductSelected(
      newLinearBundleState,
      { value: isAllPoolConditionMatched },
      isAllPoolConditionMatched,
      checkForTotalMinPoolQty(),
      { value: true },
      isAllPoolConditionMatched,
    );
    setBundleDataState({ ...updatedBundleState });
  };

  const productCardHandler = (slot: SlotDataType, cardType: CardType, parentStockStatus?: DerivedAvailabilityStatus) =>
    slot.products?.map((product: ProductData) => {
      const pd = productDataTransformer(product);
      return (
        <BundleProductCard
          key={pd.productId + pd.productName + parentStockStatus}
          data={pd}
          cardType={cardType}
          slot={slot}
          quantityHandler={quantityHandler}
          handlePriceUpdateOnVariantUpdate={(newBundleState: BundleDataType) => {
            setBundleDataState(newBundleState);
            onBundleProductSelected(
              getLinearBundleState(newBundleState),
              { value: true },
              checkForAllPoolConditionMatched(newBundleState),
              checkForTotalMinPoolQty(),
              { value: true },
              true,
            );
          }}
          parentStockStatus={parentStockStatus}
          bundleDataState={bundleDataState}
          showSubText={showSubText}
          dateSubText={dateSubText}
          skipChecksForButton={skipChecksForButton}
          showZeroAsFree={showZeroAsFree}
        />
      );
    });

  return (
    <>
      {bundle && bundleDataState && (
        <div className={styles['cpw-bundle-component-container']} data-testid="bundle-component-testId">
          {bundleDataState?.slots?.map((slot: SlotDataType, index: number) => (
            <div key={`slot${slot.id}`}>
              {slot.type === SlotType.FIXED && (
                <div className={styles['cpw-slot-container']} data-testid="slot-container-testId">
                  {index === findFixedIndex && (
                    <div className={`${styles['bundle-title']} ${styles['fixed']}`} data-testid="bundle-title-testId">
                      {slot.title}
                    </div>
                  )}
                  <div
                    className={`${styles['bundle-product-details']} ${styles['fixed']} ${
                      index + 1 === bundle?.slots?.length && styles['border-seperator']
                    }`}
                    data-testid="bundle-product-details"
                  >
                    {productCardHandler(slot, CardType.NORMAL, bundleDataState?.derivedAvailabilityStatus)}
                  </div>
                </div>
              )}
              {slot.type === SlotType.VARIANT && (
                <div
                  className={`${styles['cpw-slot-container']} ${styles['variable']}`}
                  data-testid="slot-container-variant-testId"
                >
                  <div className={styles['bundle-title']} data-testid="bundle-title-variant-testId">
                    ({slot.products?.length} {t('product.bundle.pieces')}) {slot.title}
                  </div>
                  <div className={styles['bundle-instruction']} data-testid="bundle-instruction-variant-testId">
                    {replaceSlotVariantInstructionPlaceholders(slot, t)}
                  </div>
                  <div className={styles['bundle-product-details']} data-testid="bundle-product-details">
                    {productCardHandler(slot, CardType.DROPDOWN, bundleDataState?.derivedAvailabilityStatus)}
                  </div>
                </div>
              )}
              {slot.type === SlotType.POOL && (
                <div
                  className={`${styles['cpw-slot-container']} ${styles['variable']}`}
                  data-testid="slot-container-pool-testId"
                >
                  <div className={styles['bundle-title']} data-testid="bundle-title-pool-testId">
                    ({slot.totalQty}/{slot.maxQty}) {slot.title}
                  </div>
                  <div className={styles['bundle-instruction']} data-testid="bundle-instruction-pool-testId">
                    {replaceSlotPoolInstructionPlaceholders(slot, t)}
                  </div>
                  <div className={styles['bundle-product-details']} data-testid="bundle-product-details">
                    {productCardHandler(slot, CardType.QTYSTEPPER, bundleDataState?.derivedAvailabilityStatus)}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default BundleComponent;
export type { BundleComponentPropsTypes, IBundleState };
export { SlotType };
